/**
 * @fileoverview Error classes for the Joyfill formulas engine
 * 
 * Provides typed error objects for all formula runtime failure modes.
 * Using custom error classes allows consumers to handle specific errors
 * with instanceof checks instead of brittle string matching.
 */

/**
 * Thrown when formulas reference each other in a cycle (A → B → A)
 * This prevents infinite recursion during formula evaluation.
 */
export class CircularDependencyError extends Error {
  /**
   * @param {string[]} cycle - Array of fieldIds forming the circular dependency
   */
  constructor(cycle) {
    super(`Circular dependency detected: ${cycle.join(' → ')}`);
    this.name = 'CircularDependencyError';
    this.cycle = cycle;
  }
}

/**
 * Thrown when a formula references a field that doesn't exist
 * This helps catch typos in field references within formulas.
 */
export class UnknownFieldError extends Error {
  /**
   * @param {string} fieldId - The unknown field that was referenced
   * @param {string} formulaId - The formula that contains the bad reference
   */
  constructor(fieldId, formulaId) {
    super(`Formula "${formulaId}" references unknown field: ${fieldId}`);
    this.name = 'UnknownFieldError';
    this.fieldId = fieldId;
    this.formulaId = formulaId;
  }
}

/**
 * Thrown when a formula fails to evaluate due to syntax or runtime errors
 * This wraps JavaScript evaluation errors to provide context about which formula failed.
 */
export class FormulaEvalError extends Error {
  /**
   * @param {string} message - The underlying error message
   * @param {string} formulaId - The formula that failed to evaluate
   */
  constructor(message, formulaId) {
    super(`Formula "${formulaId}" evaluation failed: ${message}`);
    this.name = 'FormulaEvalError';
    this.formulaId = formulaId;
    this.originalMessage = message;
  }
}

/**
 * Thrown when a formula references an unknown function that doesn't exist in builtins
 * Provides suggestions for similar function names to help users fix typos.
 */
export class UnknownFunctionError extends Error {
  /**
   * @param {string} functionName - The unknown function name that was called
   * @param {string} formulaId - The formula that contains the unknown function
   * @param {string[]} suggestions - Array of suggested function names
   */
  constructor(functionName, formulaId, suggestions = []) {
    const suggestionText = suggestions.length > 0 
      ? ` Did you mean: ${suggestions.join(', ')}?`
      : '';
    super(`Formula "${formulaId}" references unknown function "${functionName}".${suggestionText}`);
    this.name = 'UnknownFunctionError';
    this.functionName = functionName;
    this.formulaId = formulaId;
    this.suggestions = suggestions;
  }
}

/**
 * Thrown when a formula references undefined variables, typically in lambda functions
 * where parameter names don't match the variables used in the function body.
 */
export class UndefinedVariableError extends Error {
  /**
   * @param {string} variableName - The undefined variable that was referenced
   * @param {string} formulaId - The formula that contains the undefined variable
   * @param {string[]} availableVars - Array of variables that are defined in the current scope
   */
  constructor(variableName, formulaId, availableVars = []) {
    const availableText = availableVars.length > 0 
      ? ` Available variables: ${availableVars.join(', ')}`
      : '';
    super(`Formula "${formulaId}" references undefined variable "${variableName}".${availableText}`);
    this.name = 'UndefinedVariableError';
    this.variableName = variableName;
    this.formulaId = formulaId;
    this.availableVars = availableVars;
  }
}

/**
 * Thrown when a formula uses a reserved function name as a variable
 * This helps catch cases where users try to use function names like 'sum' or 'map' as field references.
 */
export class ReservedWordError extends Error {
  /**
   * @param {string} reservedWord - The reserved word that was misused
   * @param {string} formulaId - The formula that contains the misused reserved word
   */
  constructor(reservedWord, formulaId) {
    super(`Formula "${formulaId}" uses reserved function name "${reservedWord}" as a variable. Use ${reservedWord}() with parentheses to call the function.`);
    this.name = 'ReservedWordError';
    this.reservedWord = reservedWord;
    this.formulaId = formulaId;
  }
}